import { useState } from "react";
import { ArrowRight, CheckCircle, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";
import { usePublicSite } from "@/context/PublicSiteContext";
import { submitEnrollmentInquiry } from "@/lib/publicSite";
import Seo from "@/components/Seo";
import { siteOgImage, siteUrl } from "@/lib/siteMeta";

/**
 * Public contact / enrollment-inquiry form. Keep it trustworthy and
 * low-friction: minimum required fields, clear error states, explicit
 * success confirmation instead of a silent toast.
 *
 * On success, the inquiry is persisted via rpc_submit_enrollment_inquiry
 * and the backend fans out an admin notification through the existing
 * Resend + notifications_log pipeline — so each admin sees it in their
 * in-app Notification Center too.
 */
export default function SiteContact() {
  const { site, orgSlug } = usePublicSite();
  const [form, setForm] = useState({
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    child_age: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);     // 'ok' | 'err'
  const [errMsg, setErrMsg] = useState(null);

  if (!site) return null;
  const { org } = site;
  const siteName = org.display_name || org.name;

  const seo = (
    <Seo
      title={`Contact · ${siteName}`}
      description={`Get in touch with ${siteName} about enrollment, tours, and your family's questions.`}
      canonical={siteUrl(orgSlug, "contact")}
      ogImage={siteOgImage(orgSlug, "contact")}
      ogType="website"
      themeColor={org.brand_primary_hex}
      siteName={siteName}
    />
  );

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErrMsg(null);
    try {
      await submitEnrollmentInquiry({ org_slug: orgSlug, ...form });
      setResult("ok");
    } catch (err) {
      setErrMsg(err?.message || "Something went wrong. Please try again.");
      setResult("err");
    } finally {
      setBusy(false);
    }
  };

  if (result === "ok") {
    return (
      <>
        {seo}
        <div
          data-testid="public-contact-success"
          className="mx-auto max-w-2xl rounded-2xl border border-neutral-200 bg-white p-10 text-center"
        >
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
            style={{
              background: "color-mix(in srgb, var(--color-primary) 14%, transparent)",
              color: "var(--color-primary)",
            }}
          >
            <CheckCircle size={28} weight="duotone" />
          </div>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-neutral-900 md:text-3xl">
            Thanks — we'll be in touch soon.
          </h1>
          <p className="mt-3 text-sm text-neutral-600 md:text-base">
            Your note went to the {org.display_name || org.name} team. You'll hear back within one business day.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {seo}
      <div data-testid="public-contact" className="grid gap-10 md:grid-cols-[1.2fr_0.8fr]">
      <section>
        <header>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: "var(--color-primary)" }}
          >
            Say hello
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 md:text-4xl">
            Tell us a little about your family.
          </h1>
          <p className="mt-3 max-w-xl text-sm text-neutral-600 md:text-base">
            A short note is all we need. We'll follow up with a time to visit.
          </p>
        </header>

        <form
          data-testid="public-contact-form"
          onSubmit={onSubmit}
          className="mt-8 space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 md:p-7"
        >
          <Field label="Your name" testid="contact-name-field">
            <input
              type="text"
              name="name"
              autoComplete="name"
              required
              value={form.parent_name}
              onChange={onChange("parent_name")}
              data-testid="contact-name-input"
              className="input"
              placeholder="Alex Rivera"
              disabled={busy}
            />
          </Field>
          <div className="grid gap-5 md:grid-cols-2">
            <Field label="Email" testid="contact-email-field">
              <input
                type="email"
                name="email"
                autoComplete="email"
                required
                value={form.parent_email}
                onChange={onChange("parent_email")}
                data-testid="contact-email-input"
                className="input"
                placeholder="you@example.com"
                disabled={busy}
              />
            </Field>
            <Field label="Phone (optional)" testid="contact-phone-field">
              <input
                type="tel"
                name="tel"
                autoComplete="tel"
                value={form.parent_phone}
                onChange={onChange("parent_phone")}
                data-testid="contact-phone-input"
                className="input"
                placeholder="(555) 123-4567"
                disabled={busy}
              />
            </Field>
          </div>
          <Field label="Child's age (optional)" testid="contact-age-field">
            <input
              type="text"
              name="child_age"
              autoComplete="off"
              value={form.child_age}
              onChange={onChange("child_age")}
              data-testid="contact-age-input"
              className="input"
              placeholder="e.g. 2.5 years"
              disabled={busy}
            />
          </Field>
          <Field label="What would you like us to know?" testid="contact-message-field">
            <textarea
              required
              name="message"
              rows={5}
              value={form.message}
              onChange={onChange("message")}
              data-testid="contact-message-input"
              className="input resize-y"
              placeholder="A few things about your family, your schedule, questions you have…"
              disabled={busy}
            />
          </Field>

          {errMsg && (
            <p
              data-testid="public-contact-error"
              className="rounded-xl border border-[#C6851E]/40 bg-[#C6851E]/10 px-3 py-2 text-xs text-[#8A5A12]"
            >
              {errMsg}
            </p>
          )}

          <div className="flex items-center justify-between pt-2">
            <p className="text-[11px] text-neutral-500">
              We'll only use your info to reply about enrollment.
            </p>
            <button
              type="submit"
              disabled={busy}
              data-testid="public-contact-submit"
              className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:brightness-105 disabled:opacity-60"
              style={{ background: "var(--color-primary)" }}
            >
              {busy ? "Sending…" : "Send inquiry"}
              {!busy && <ArrowRight size={14} weight="bold" />}
            </button>
          </div>
        </form>
      </section>

      <aside className="space-y-5">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6">
          <p
            className="text-[10px] font-bold uppercase tracking-[0.18em]"
            style={{ color: "var(--color-primary)" }}
          >
            Reach us directly
          </p>
          <ul className="mt-4 space-y-4 text-sm text-neutral-700">
            {org.contact_email && (
              <ContactRow icon={EnvelopeSimple}>
                <a
                  href={`mailto:${org.contact_email}`}
                  className="underline hover:text-neutral-900"
                >
                  {org.contact_email}
                </a>
              </ContactRow>
            )}
            {org.contact_phone && (
              <ContactRow icon={Phone}>{org.contact_phone}</ContactRow>
            )}
            {org.contact_address && (
              <ContactRow icon={MapPin}>{org.contact_address}</ContactRow>
            )}
            {!org.contact_email && !org.contact_phone && !org.contact_address && (
              <li className="text-sm text-neutral-500">
                Our contact details will appear here once the center adds them.
              </li>
            )}
          </ul>
        </div>

        <div
          className="rounded-2xl border p-6 text-sm leading-relaxed text-neutral-700"
          style={{
            borderColor: "color-mix(in srgb, var(--color-primary) 30%, transparent)",
            background: "color-mix(in srgb, var(--color-primary) 6%, #fff)",
          }}
        >
          <p className="font-heading text-base font-semibold text-neutral-900">
            What happens next
          </p>
          <ol className="mt-3 space-y-2 pl-5 [list-style:decimal]">
            <li>We review your note the same day it arrives.</li>
            <li>A team member replies by email within one business day.</li>
            <li>We set up a short visit at a time that works for you.</li>
          </ol>
        </div>
      </aside>

      <style>{`
        .input {
          display: block;
          width: 100%;
          padding: 0.65rem 0.9rem;
          font-size: 0.95rem;
          color: #1f2937;
          background: #fff;
          border: 1px solid #E5E7EB;
          border-radius: 0.9rem;
          transition: border-color .12s ease, box-shadow .12s ease;
        }
        .input:focus {
          outline: none;
          border-color: var(--color-primary, #5A7E46);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary) 18%, transparent);
        }
      `}</style>
      </div>
    </>
  );
}

function Field({ label, testid, children }) {
  return (
    <label data-testid={testid} className="block">
      <span className="mb-1.5 block text-xs font-semibold text-neutral-700">
        {label}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-xl"
        style={{
          background: "color-mix(in srgb, var(--color-primary) 12%, transparent)",
          color: "var(--color-primary)",
        }}
      >
        <Icon size={14} weight="duotone" />
      </span>
      <div>{children}</div>
    </li>
  );
}
